import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BackgroundColorProps, HeightProps, ResponsiveValue } from 'styled-system';
import { backgroundColor as backgroundColorStyle, height, space } from 'styled-system';

import { linkStyles } from 'common/components/Link';
import RichTextBlock from 'common/components/RichText';
import {
  BodyRegularBR1,
  TYPOGRAPHY_BR1_FONT_SIZE,
  TYPOGRAPHY_BR1_LINE_HEIGHT,
} from 'common/components/Typography';
import type { Color } from 'common/theme/colors/types';
import { focusOutlineCSSWithInwardOffset } from 'common/theme/styles';
import type { OutlineProps } from 'common/theme/system-types';
import type { BoxSizingProps, OverflowXProps, OverflowYProps } from 'common/theme/system-utilities';
import {
  boxSizing,
  outline,
  overflowX,
  overflowY,
  textDecoration,
} from 'common/theme/system-utilities';

interface ContentProps {
  content?: React.ReactNode;
  html?: string;
  p?: string;
  backgroundColor?: ResponsiveValue<Color>;
  withBodyFocus?: boolean;
}

const Container = styled(BodyRegularBR1)<
  HeightProps &
    BoxSizingProps &
    OverflowXProps &
    OverflowYProps &
    OutlineProps &
    BackgroundColorProps
>`
  ${height};
  ${boxSizing};
  ${overflowX};
  ${overflowY};
  ${outline};
  ${backgroundColorStyle};
  ${focusOutlineCSSWithInwardOffset};
`;

const containerStyles: ComponentProps<typeof Container> = {
  height: '100%',
  boxSizing: 'border-box',
  overflowY: 'auto',
  overflowX: 'hidden',
  outline: 'none',
};

const StyledRichTextBlock = styled(RichTextBlock)`
  ${space};
  a {
    ${textDecoration};
    ${linkStyles};
    color: inherit;
  }
`;
const styledRichTextBlockStyles = {
  p: ['sp_16', 'sp_24', null, 'sp_32'],
  fontSize: TYPOGRAPHY_BR1_FONT_SIZE,
  lineHeight: TYPOGRAPHY_BR1_LINE_HEIGHT,
  textDecoration: 'underline',
  styled: true,
};

const Content = ({ html, content, p, backgroundColor, withBodyFocus = true }: ContentProps) => (
  <Container
    {...containerStyles}
    data-testid="drawer-body"
    {...(withBodyFocus && { tabIndex: 0 })}
    {...(backgroundColor && { backgroundColor })}
  >
    {content || (
      <StyledRichTextBlock
        p={p}
        dangerouslySetInnerHTML={{ __html: html }}
        data-testid="html"
        {...styledRichTextBlockStyles}
      />
    )}
  </Container>
);

export default Content;
